/**
 * Core types for the support chat system
 *
 * All types are strongly typed with no `any` types.
 * All string IDs use branded types to prevent mixing.
 */

import type { BrowserContext } from '../lib/browser-context-format';

/**
 * Branded type for thread IDs (Slack thread_ts)
 */
export type ThreadId = string & { readonly __brand: 'ThreadId' };

/**
 * Branded type for channel IDs (Slack channel ID)
 */
export type ChannelId = string & { readonly __brand: 'ChannelId' };

/**
 * Branded type for message IDs (Slack message ts)
 */
export type MessageId = string & { readonly __brand: 'MessageId' };

/**
 * Branded type for Cognito user sub
 */
export type CognitoSub = string & { readonly __brand: 'CognitoSub' };

/**
 * ISO 8601 timestamp string
 */
export type Timestamp = string & { readonly __brand: 'Timestamp' };

/**
 * Message sender type
 */
export type MessageSenderType = 'user' | 'support' | 'system';

/**
 * Message sender information
 */
export interface MessageSender {
  /** Sender type: 'user' (from app), 'support' (from Slack), or 'system' */
  type: MessageSenderType;
  /** Sender name */
  name: string;
  /** Sender email (if available) */
  email?: string;
}

/**
 * File attachment structure
 */
export interface FileAttachment {
  /** Unique file ID from Slack */
  id: string;
  /** File name */
  name: string;
  /** URL to access the file */
  url: string;
  /** MIME type of the file */
  mimeType: string;
  /** File size in bytes */
  size: number;
}

/**
 * Chat message structure
 */
export interface ChatMessage {
  /** Unique message ID (Slack message ts) */
  id: MessageId;
  /** Message text content */
  text: string;
  /** Sender information */
  sender: MessageSender;
  /** Message timestamp (ISO 8601) */
  timestamp: Timestamp;
  /** Whether this is a user message */
  isUser: boolean;
  /** File attachments (if any) */
  attachments?: FileAttachment[];
}

/**
 * Support thread metadata stored in Cognito
 */
export interface SupportThread {
  /** Thread ID (Slack thread_ts) */
  threadId: ThreadId;
  /** Channel ID where thread exists */
  channelId: ChannelId;
  /** Thread creation timestamp */
  createdAt: Timestamp;
  /**
   * Signed grant authorizing this thread (from the create-thread response).
   * Must be persisted with the thread and sent on every read/write — the server
   * resolves the thread from this rather than from a client-supplied id. A
   * stored thread without a grant cannot be continued.
   */
  grant?: string;
  /** Optional app-specific metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Cookie storage configuration for unauthenticated users
 */
export interface CookieStorageConfig {
  /** Cookie name for storing thread list */
  cookieName: string;
  /** Cookie expiration in days (default: 365) */
  maxAge?: number;
  /** Cookie path (default: '/') */
  path?: string;
  /** Whether cookie is secure (HTTPS only, default: true in production) */
  secure?: boolean;
  /** SameSite attribute (default: 'lax') */
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Support chat configuration
 * 
 * ⚠️ SECURITY WARNING: The `botToken` field should NEVER be included in client-side code.
 * It is a secret credential that must only be used in server-side API route handlers.
 * Client-side components should only provide: channelId, apiBaseUrl, pollInterval, and cookieStorage.
 */
export interface SupportChatConfig {
  /** Slack channel ID where support threads will be created */
  channelId: ChannelId;
  /** 
   * Slack bot token for API authentication
   * ⚠️ SECURITY: NEVER include this in client-side code. Only use in server-side API route handlers.
   * Client-side components should omit this field entirely.
   */
  botToken?: string;
  /** API base URL for the support endpoints (relative to app, e.g., '/api/support') */
  apiBaseUrl?: string;
  /** Polling interval in milliseconds (default: 2500) */
  pollInterval?: number;
  /** Custom attribute name for storing thread list in Cognito (default: 'support_threads') */
  threadAttributeName?: string;
  /** Cookie storage configuration for unauthenticated users (optional) */
  cookieStorage?: CookieStorageConfig;
}

/**
 * Support chat user information
 */
export interface SupportChatUser {
  /** User's email address */
  email: string;
  /** Cognito user sub (unique identifier) - required for authenticated users */
  cognitoSub?: CognitoSub;
  /** User's display name (optional) */
  name?: string;
  /** Cognito access token for client-side attribute updates - required for authenticated users */
  accessToken?: string;
  /** Whether user is authenticated (if false, will use cookie storage if configured) */
  isAuthenticated?: boolean;
}

/**
 * Optional, OPT-IN feature configuration for the support chat widget.
 *
 * ⚠️ SCOPING: every field here is undefined by default. The shared widget is
 * consumed by ~22 apps; a feature only turns on for an app that explicitly
 * passes the corresponding prop. Hub passes these; no other app does. Do NOT
 * default any of these to "on" — that would silently enable the feature
 * everywhere the package is used.
 */

/** Context handed to the offline-fallback "convert to ticket" callback. */
export interface SupportChatConvertContext {
  /** Slack thread id of the chat being converted. */
  threadId: string;
  /** The chat user's email. */
  userEmail: string;
  /** The chat user's display name, when known. */
  userName?: string;
  /** Plain-text transcript of the visible conversation. */
  transcript: string;
}

/** Result of an offline-fallback convert-to-ticket callback. */
export interface SupportChatConvertResult {
  /** Whether the conversion succeeded. */
  ok: boolean;
  /** A short, customer-safe message to show after conversion. */
  message?: string;
}

/**
 * Offline fallback: when the customer has waited `replyTimeoutMs` without a
 * human agent reply, the widget surfaces a "turn this into a ticket" button
 * wired to `onConvertToTicket`. Opt-in — pass this prop to enable it.
 */
export interface SupportChatOfflineFallbackOptions {
  /** Wait this long (ms) after the customer's last message with no agent reply before showing the button. Default 120000 (2 min). */
  replyTimeoutMs?: number;
  /** Performs the conversion (host app: create a ticket + send an acknowledgement email). */
  onConvertToTicket: (
    ctx: SupportChatConvertContext
  ) => Promise<SupportChatConvertResult>;
}

/**
 * Post-chat satisfaction rating. When provided, the widget shows a 👍/👎 prompt
 * once a real conversation has happened. Opt-in — pass this prop to enable it.
 */
export interface SupportChatRatingOptions {
  /** Persist the rating (host app: write to its feedback store). */
  onRate: (
    rating: 'up' | 'down',
    ctx: { threadId: string; userEmail: string }
  ) => Promise<void> | void;
}

/**
 * Thread information
 */
export interface ThreadInfo {
  /** Thread ID (Slack thread_ts) */
  threadId: ThreadId;
  /** Channel ID where thread exists */
  channelId: ChannelId;
  /** Thread creation timestamp */
  createdAt: Timestamp;
  /**
   * Signed grant authorizing this thread. Required on every subsequent read and
   * write; persist it with the thread.
   */
  grant?: string;
}

/**
 * Create thread request
 */
export interface CreateThreadRequest {
  /** User's email */
  userEmail: string;
  /** Cognito user sub (optional for unauthenticated users) */
  cognitoSub?: CognitoSub;
  /** Optional initial message (intro + the customer's first message only). */
  initialMessage?: string;
  /**
   * Structured browser context. When present, the server renders it as a
   * compact Slack Block Kit `context` block appended below the intake message
   * — NOT bulleted into the message body. Kept structured (not pre-formatted
   * text) so the block form is what Slack renders.
   */
  browserContext?: BrowserContext;
}

/**
 * Create thread success response
 */
export interface CreateThreadResponse {
  /** Thread ID (Slack thread_ts) */
  threadId: ThreadId;
  /** Channel ID */
  channelId: ChannelId;
  /** Creation timestamp */
  createdAt: Timestamp;
  /**
   * Signed grant authorizing reads and writes on this thread. The client MUST
   * persist this with the thread — every later request needs it, and it cannot
   * be re-issued.
   */
  grant: string;
}

/**
 * Create thread error response
 */
export interface CreateThreadErrorResponse {
  error: string;
}

/**
 * Send message request
 * 
 * Note: When sending files, use FormData with:
 * - threadId: string
 * - message: string
 * - userEmail: string
 * - cognitoSub?: string
 * - files: File[] (multiple files can be attached)
 */
export interface SendMessageRequest {
  /**
   * Thread ID to post to.
   *
   * @deprecated The server IGNORES this and resolves the thread from
   * `threadGrant` instead — a Slack thread_ts is an enumerable timestamp, so
   * trusting it let anyone post into another customer's conversation. Kept only
   * so existing callers still type-check; it has no effect.
   */
  threadId: ThreadId;
  /** Message text */
  message: string;
  /**
   * User's email.
   *
   * @deprecated Also ignored — the sender identity comes from `threadGrant`.
   */
  userEmail: string;
  /** Cognito user sub (optional for unauthenticated users) */
  cognitoSub?: CognitoSub;
  /**
   * The signed thread grant issued by the create-thread endpoint. REQUIRED —
   * without it the request is refused. May also be sent as the
   * `x-support-thread-grant` header, which is preferred (query strings and
   * bodies are likelier to be logged).
   */
  threadGrant?: string;
}

/**
 * Send message success response
 */
export interface SendMessageResponse {
  /** Whether message was sent successfully */
  success: true;
  /** Message ID from Slack */
  messageId: MessageId;
  /** Message timestamp */
  timestamp: Timestamp;
}

/**
 * Send message error response
 */
export interface SendMessageErrorResponse {
  success: false;
  error: string;
}

/**
 * Get messages request (threadId comes from URL param)
 */
export interface GetMessagesRequest {
  threadId: ThreadId;
}

/**
 * Get messages success response
 */
export interface GetMessagesResponse {
  /** Array of messages in the thread */
  messages: ChatMessage[];
  /** Last update timestamp */
  lastUpdated: Timestamp;
}

/**
 * Get messages error response
 */
export interface GetMessagesErrorResponse {
  error: string;
}

/**
 * Generic API success response
 */
export interface ApiSuccessResponse<T = unknown> {
  success: true;
  data: T;
}

/**
 * Generic API error response
 */
export interface ApiErrorResponse {
  success: false;
  error: string;
  code?: string;
}

/**
 * Client-side attribute update request
 */
export interface UpdateThreadListRequest {
  /** Cognito access token */
  accessToken: string;
  /** Custom attribute name (default: 'support_threads') */
  attributeName?: string;
  /** Updated thread list */
  threads: SupportThread[];
}

/**
 * Client-side attribute update response
 */
export interface UpdateThreadListResponse {
  success: boolean;
  error?: string;
}

/**
 * Type guard to check if response is an error
 */
export function isErrorResponse(
  response: unknown
): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    response.success === false &&
    'error' in response &&
    typeof response.error === 'string'
  );
}

/**
 * Type guard to check if CreateThreadResponse is valid
 */
export function isValidCreateThreadResponse(
  response: unknown
): response is CreateThreadResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'threadId' in response &&
    'channelId' in response &&
    'createdAt' in response &&
    typeof (response as CreateThreadResponse).threadId === 'string' &&
    typeof (response as CreateThreadResponse).channelId === 'string' &&
    typeof (response as CreateThreadResponse).createdAt === 'string'
  );
}

/**
 * Type guard to check if SendMessageResponse is success
 */
export function isSendMessageSuccess(
  response: SendMessageResponse | SendMessageErrorResponse
): response is SendMessageResponse {
  return response.success === true;
}

/**
 * Helper to create a ThreadId from string
 */
export function toThreadId(value: string): ThreadId {
  return value as ThreadId;
}

/**
 * Helper to create a ChannelId from string
 */
export function toChannelId(value: string): ChannelId {
  return value as ChannelId;
}

/**
 * Helper to create a MessageId from string
 */
export function toMessageId(value: string): MessageId {
  return value as MessageId;
}

/**
 * Helper to create a CognitoSub from string
 */
export function toCognitoSub(value: string): CognitoSub {
  return value as CognitoSub;
}

/**
 * Helper to create a Timestamp from string
 */
export function toTimestamp(value: string): Timestamp {
  return value as Timestamp;
}
